// Every retainer, with its P&L. This is the income layer: the engagement is billed as
// a whole and the jobs beneath it hold the work, so profit resolves here — a retainer's
// revenue minus the costs of every topic under it.
//
// THE DEFAULT VIEW IS THE LIVE ONES. A closed engagement is history: it will never bill
// again, and mixed in with the live ones it makes the list longer without making it more
// informative. The toggle keeps it one click away, never gone — a closed retainer's money
// is still in every figure on this screen and everywhere else.
//
// THE FILTER IS `status`, NEVER THE TERM. A retainer whose end_date has passed is still
// active and still on this list, wearing the red "ההתקשרות הסתיימה" badge that asks
// somebody to decide. That is the point: a date passing is a fact about the paperwork,
// while completed / cancelled is a decision a person made. If a lapsed term hid a
// retainer by itself, an engagement that quietly rolled on past its contract — which is
// routine — would vanish from the screen precisely when it most needs looking at, and
// the renew-or-close task would be the only trace left. Nothing anywhere sets the status
// from the clock.
//
// ONE SCREEN, TWO LISTS (migration 067). `billing="monthly"` is the retainers screen;
// `billing="fixed"` is the one-off projects screen — engagements billed one fixed amount
// for their whole term, with jobs beneath them exactly as a retainer has. The rows, the
// profit, the close action and the tabs are the same component on purpose; only the words
// that name the engagement and the amount column (monthly vs the project's price) differ.
// Each screen creates the kind it lists, so a new project never lands on the retainers
// screen or the other way round.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { retainersApi } from '../../api';
import { engagementPath } from '../../components/engagement';
import he from '../../i18n/he';
import { issuerName } from '../../utils/issuer';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Money, { Profit } from '../../components/Money';
import Button from '../../components/Button';
import { NetBadge } from '../../components/ExpenseSplit';
import { TermCell } from '../../components/RetainerTerm';
import RetainerModal from './RetainerModal';
import RetainerStatusModal from './RetainerStatusModal';

export default function Retainers({ billing = 'monthly' }) {
  const navigate = useNavigate();
  const lookups = useLookups();
  const fixed = billing === 'fixed';
  const t = fixed ? he.projects : he.retainers;
  const { data, loading, error, reload } = useAsync(() => retainersApi.list({ billing }), [billing]);
  const [adding, setAdding] = useState(false);
  // Which retainer is being closed or reopened — the row, not just its id, because the
  // dialog names it and needs its current status to know which question it is asking.
  const [closing, setClosing] = useState(null);
  // A VIEW filter and nothing else, the same shape the clients screen uses: both halves
  // arrive in the one fetch and are split here, so switching is instant and a closed
  // retainer is never actually gone, just on the other tab.
  const [showClosed, setShowClosed] = useState(false);

  const rows = (data || []).filter((r) =>
    (showClosed ? r.status !== 'active' : r.status === 'active'));

  const columns = [
    // THE CLIENT LEADS THE ROW. It is the thing the eye looks for first — this list is
    // scanned by who the engagement is for, not by what it was named — so the client sits
    // at the start of the row and carries the weight, and on a phone it is the card's
    // headline. The retainer's own name follows it as an ordinary labelled field.
    { key: 'client_name', label: he.jobs.client, primary: true, className: 'strong' },
    { key: 'title', label: fixed ? t.projectTitle : t.retainerTitle },
    { key: 'revenue_stream', label: he.jobs.stream, render: (r) => lookups.streamLabel(r.revenue_stream) },
    { key: 'issuing_partner_name', label: he.jobs.defaultIssuerShort, render: (r) => issuerName(r.issuing_partner_name) },
    fixed
      ? { key: 'fixed_amount', label: t.price, className: 'num', render: (r) => <Money value={r.fixed_amount} /> }
      : { key: 'monthly_amount', label: t.monthlyAmount, className: 'num', render: (r) => <Money value={r.monthly_amount} /> },
    { key: 'job_count', label: he.retainers.jobCount, className: 'num' },
    // The term is shown on BOTH tabs and reads the same on each: it is the contract's
    // dates, not a status. On a closed retainer it is simply the history of when the
    // engagement ran.
    { key: 'term', label: t.endDate, render: (r) => <TermCell term={r.term} status={r.status} /> },
    { key: 'status', label: he.jobs.status, render: (r) => <StatusBadge kind="retainer" value={r.status} /> },
    // Profit is the accrual figure, unchanged. The badge reads it NET of the costs the
    // client bears and nobody has charged on yet — money coming back, which never really
    // reduced what this engagement made. It appears only on the rows that have such a
    // cost, and disappears the moment the charge is raised.
    {
      key: 'profit',
      label: he.jobs.profit,
      className: 'num',
      render: (r) => (
        <>
          <Profit value={r.profit} />
          <NetBadge profit={r.profit} toRecover={r.expensesToRecover} />
        </>
      ),
    },
    // Closing is offered on the row, because deciding an engagement is over is something
    // you do while scanning the list — usually right after the term badge beside it made
    // you look. stopPropagation, or the click would also open the retainer underneath.
    {
      key: 'close',
      label: '',
      action: true,
      render: (r) => (
        <Button
          small
          variant="subtle"
          onClick={(e) => { e.stopPropagation(); setClosing(r); }}
        >
          {r.status === 'active' ? he.retainers.closeRetainerAction : he.retainers.reopenRetainer}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t.title} spread>
        <div className="seg">
          <Button small variant={showClosed ? 'subtle' : ''} onClick={() => setShowClosed(false)}>
            {he.retainers.showActive}
          </Button>
          <Button small variant={showClosed ? '' : 'subtle'} onClick={() => setShowClosed(true)}>
            {he.retainers.showClosed}
          </Button>
        </div>
        <Button onClick={() => setAdding(true)}>{fixed ? t.newProject : t.newRetainer}</Button>
      </PageHeader>
      <div className="card">
        <AsyncBoundary loading={loading} error={error}>
          <DataTable
            columns={columns}
            rows={rows}
            onRowClick={(r) => navigate(engagementPath(r))}
            empty={{ text: showClosed ? t.emptyClosed : t.emptyActive }}
          />
        </AsyncBoundary>
      </div>
      {adding && (
        <RetainerModal billing={billing} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />
      )}
      {closing && (
        <RetainerStatusModal
          retainer={closing}
          onClose={() => setClosing(null)}
          onSaved={() => { setClosing(null); reload(); }}
        />
      )}
    </>
  );
}
